import React, { useState, useCallback } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSession } from '../hooks/useSession';
import api from '../utils/api';
import toast from 'react-hot-toast';
import PhotoUpload from './PhotoUpload';
import PhotoLightbox from './PhotoLightbox';

const PhotoGallery = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const { data: session } = useSession(sessionId);
  const [showUpload, setShowUpload] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const isHost = session?.hostId === user?.uid;

  // Photos come from session cache (updated via socket events)
  const photos = React.useMemo(() => {
    const photosObj = session?.photos || {};
    return Object.values(photosObj).sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [session?.photos]);

  const handleDelete = useCallback(async (photoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/api/sessions/${sessionId}/photos/${photoId}`);
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  }, [sessionId]);

  const formatTime = useCallback((timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  if (photos.length === 0) {
    return (
      <div className="card p-8 text-center">
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-400 mb-1">No photos yet</p>
        <p className="text-gray-400 text-sm mb-4">Be the first to share a moment!</p>
        <button
          onClick={() => setShowUpload(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
        >
          <Camera className="w-4 h-4" />
          Share Photo
        </button>
        {showUpload && <PhotoUpload onClose={() => setShowUpload(false)} />}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">
            Photos <span className="text-gray-400 text-sm font-normal">({photos.length})</span>
          </h3>
          <p className="text-xs text-gray-400">Photos disappear after 24 hours</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm"
        >
          <Camera className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative group cursor-pointer aspect-square rounded-lg overflow-hidden bg-gray-700"
            onClick={() => setLightboxPhoto(photo)}
          >
            <img
              src={photo.url}
              alt={photo.caption || 'Session photo'}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-0 left-0 right-0 p-2">
                <p className="text-white text-xs font-medium truncate">{photo.senderName}</p>
                <p className="text-gray-300 text-xs">{formatTime(photo.timestamp)}</p>
              </div>
            </div>
            {isHost && (
              <button
                onClick={(e) => handleDelete(photo.id, e)}
                className="absolute top-2 right-2 bg-red-600/80 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1 sm:hidden">
                <p className="text-white text-xs truncate">{photo.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {showUpload && <PhotoUpload onClose={() => setShowUpload(false)} />}
      {lightboxPhoto && <PhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />}
    </div>
  );
};

export default PhotoGallery;
